import React from 'react';
import { CheckCircle, Clock, BookOpen, HelpCircle } from 'lucide-react';

/**
 * Desktop-only sidebar listing all chapters as vertical pills.
 * - ✓ pill if validated (this session or in history)
 * - blue active pill if current
 * - Bottom: progress bar + timer
 */
const CourseSectionsSidebar = ({
    chapters,
    currentChapterIndex,
    sessionValidatedSections,
    validatedSections,
    onSelectChapter,
    progressPercent,
    sessionValidatedCount,
    formattedTime,
    getSectionTitle,
    courseTitle,
    isQuiz = false,
    coursePaused = false,
    onTogglePause,
    open = true,
    language = 'fr',
}) => {
    const fr = language === 'fr';
    return (
        <div
            data-help="course-chapters"
            className={`hidden md:block flex-shrink-0 overflow-hidden border-r border-gray-200 dark:border-white/10 bg-white dark:bg-black transition-[width] duration-300 ${open ? 'w-72' : 'w-0'}`}
        >
        <aside className="w-72 h-full flex flex-col">
            {/* Top: book icon + title (back arrow is now outside the sidebar at modal level).
                Left padding leaves room for the back arrow (absolute top-4 left-4 of the modal).
                Right padding leaves room for the burger toggle that sits at the sidebar's right edge. */}
            <div className="pl-16 pr-12 pt-5 pb-4 flex items-center gap-3">
                <div className={`p-1.5 rounded-lg flex-shrink-0 ${isQuiz ? 'bg-purple-100 dark:bg-purple-900/40' : 'bg-blue-100 dark:bg-blue-900/40'}`}>
                    {isQuiz
                        ? <HelpCircle className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        : <BookOpen className="w-4 h-4 text-blue-600 dark:text-blue-400" />}
                </div>
                {courseTitle && (
                    <p className="flex-1 min-w-0 text-sm font-bold text-gray-900 dark:text-white truncate" title={courseTitle}>
                        {courseTitle}
                    </p>
                )}
            </div>

            {/* Chapters list */}
            <div className="flex-1 overflow-y-auto custom-scrollbar px-4 pb-4 space-y-2">
                {chapters.map((ch, idx) => {
                    const validatedThisSession = sessionValidatedSections[String(ch.idChapter)] === '1';
                    const validatedHistory = validatedSections[String(ch.idChapter)] === '1';
                    const isCurrent = idx === currentChapterIndex;

                    let stateClass;
                    if (isCurrent) {
                        stateClass = 'bg-blue-600 text-white shadow-md shadow-blue-500/30';
                    } else if (validatedThisSession) {
                        stateClass = 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
                    } else if (validatedHistory) {
                        stateClass = 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300';
                    } else {
                        stateClass = 'bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10';
                    }

                    return (
                        <button
                            key={ch.idChapter || idx}
                            onClick={() => onSelectChapter(idx)}
                            className={`group w-full text-left flex items-center gap-2.5 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${stateClass}`}
                        >
                            {(validatedThisSession || validatedHistory) ? (
                                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                            ) : (
                                <span className="w-4 h-4 flex-shrink-0 rounded-full border-2 border-current opacity-60" />
                            )}
                            <span className="flex-1 truncate">
                                {getSectionTitle(ch)}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Bottom: progress + timer + session pause */}
            <div className="px-5 py-4 border-t border-gray-200 dark:border-white/10">
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                    <span className="font-bold text-blue-600 dark:text-blue-400">
                        {progressPercent}% {sessionValidatedCount}/{chapters.length}
                    </span>
                    <span className="flex items-center gap-1.5">
                        {onTogglePause && (
                            <button
                                onClick={onTogglePause}
                                title={coursePaused
                                    ? (fr ? 'Reprendre la session' : 'Resume session')
                                    : (fr ? 'Mettre en pause' : 'Pause session')}
                                className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-white/10 text-gray-600 dark:text-gray-300 transition"
                            >
                                {coursePaused ? (
                                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                    </svg>
                                ) : (
                                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                )}
                            </button>
                        )}
                        <Clock className="w-3.5 h-3.5" />
                        <span className="tabular-nums">{formattedTime}</span>
                    </span>
                </div>
                <div className="h-1.5 bg-gray-200 dark:bg-white/10 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                        style={{ width: `${progressPercent}%` }}
                    />
                </div>
            </div>
        </aside>
        </div>
    );
};

export default CourseSectionsSidebar;
